import moment from 'moment'
import ReactApexChart from 'react-apexcharts'

import { getDynamicColumnWidth } from 'components/group/consuption/helper/getDynamicColumnWidth'
import { IMilkYieldGraphItem } from 'models/IMilkYieldChartTypes'
import { COLORS } from 'styles/colors'
import Preloader from 'ui/Preloader'

import { localizationApexCharts } from 'utils'
import { getMilkYieldTooltip } from '../../../hooks/getMilkYieldTooltip'

interface MilkYieldChartProps {
	graph: IMilkYieldGraphItem[]
	daysBetweenDates: number
	shouldAggregateMonthly: boolean
	cowCount: number
}

const MilkYieldChart = ({
	graph,
	daysBetweenDates,
	shouldAggregateMonthly,
	cowCount,
}: MilkYieldChartProps) => {
	if (!graph) {
		return <Preloader />
	}

	const sellValuesForRenderInChart = graph.map(el => {
		if (el.sell_milk_sum !== null && el.reject_milk_sum !== null) {
			return el.sell_milk_sum > el.reject_milk_sum
				? el.sell_milk_sum - el.reject_milk_sum
				: el.sell_milk_sum
		} else {
			return el.sell_milk_sum
		}
	})

	let maxSeriesValue = -Infinity

	for (const data of graph) {
		const sellSum = data.sell_milk_sum ?? 0
		const rejectSum = data.reject_milk_sum ?? 0
		const sum =
			sellSum > rejectSum ? Math.round(sellSum) : Math.round(rejectSum)

		if (sum > maxSeriesValue) {
			maxSeriesValue = sum
		}
	}

	const noDataMask = graph.map(
		el =>
			(el.sell_milk_sum === null || el.sell_milk_sum === 0) &&
			(el.reject_milk_sum === null || el.reject_milk_sum === 0),
	)

	const noDataValues = graph.map((el, i) =>
		noDataMask[i] ? maxSeriesValue + 1 : null,
	)

	const rejectValuesForRenderInChart = graph.map(el => {
		if (el.sell_milk_sum !== null && el.reject_milk_sum !== null) {
			return el.reject_milk_sum > el.sell_milk_sum
				? el.reject_milk_sum - el.sell_milk_sum
				: el.reject_milk_sum
		} else {
			return el.reject_milk_sum
		}
	})

	const yearsValues: Array<{
		date: number
		sell_sum: number
		reject_sum: number
	}> = []

	const sortedData = [...graph].sort(
		(a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
	)
	// Берем первую и последнюю дату из данных
	const firstDate = moment(sortedData[0].date).startOf('month')
	const lastDate = moment(sortedData[sortedData.length - 1].date).endOf('month')
	// Вычисляем количество месяцев в диапазоне
	const monthsCount = lastDate.diff(firstDate, 'months') + 1

	for (let i = 0; i < monthsCount; i++) {
		const startDate = firstDate.clone().add(i, 'months').startOf('month')
		const endDate = firstDate.clone().add(i, 'months').endOf('month')
		const filteredData = sortedData.filter(item =>
			moment(item.date).isBetween(startDate, endDate, null, '[]'),
		)
		const sellValues = filteredData
			.map(item => item.sell_milk_sum)
			.filter((value): value is number => value != null)
		const rejectValues = filteredData
			.map(item => item.reject_milk_sum)
			.filter((value): value is number => value != null)
		const monthData = {
			date: startDate.valueOf(),
			sell_sum:
				sellValues.length > 0
					? Math.round(
							sellValues.reduce((sum, value) => sum + value, 0) /
								sellValues.length,
						)
					: 0,
			reject_sum:
				rejectValues.length > 0
					? Math.round(
							rejectValues.reduce((sum, value) => sum + value, 0) /
								rejectValues.length,
						)
					: 0,
		}
		yearsValues.push(monthData)
	}
	yearsValues.sort((a, b) => a.date - b.date)
	if (yearsValues.length > 12) {
		yearsValues.splice(0, yearsValues.length - 12)
	}

	const dataXasis = shouldAggregateMonthly
		? yearsValues.map(el => el.date)
		: graph.map(el => {
				const timestamp = +el.date
				return isNaN(timestamp) ? null : timestamp
			})

	const yearsSellValues = yearsValues?.map(el => {
		if (el.sell_sum !== null && el.reject_sum !== null) {
			return el.sell_sum > el.reject_sum
				? el.sell_sum - el.reject_sum
				: el.sell_sum
		} else {
			return el.sell_sum
		}
	})
	const yearsRejectValues = yearsValues?.map(el => {
		if (el.sell_sum !== null && el.reject_sum !== null) {
			return el.reject_sum > el.sell_sum
				? el.reject_sum - el.sell_sum
				: el.reject_sum
		} else {
			return el.reject_sum
		}
	})

	let maxYearsSeriesValue = -Infinity

	for (const data of yearsValues) {
		const sellSum = data.sell_sum !== null ? data.sell_sum : 0
		const rejectSum = data.reject_sum !== null ? data.reject_sum : 0

		const sum =
			sellSum > rejectSum ? Math.round(sellSum) : Math.round(rejectSum)

		if (sum > maxYearsSeriesValue) {
			maxYearsSeriesValue = sum
		}
	}

	const chartCategories =
		daysBetweenDates === 2 ? ['', ...dataXasis, ''] : dataXasis

	const sellValuesWithPadding =
		daysBetweenDates === 2
			? [null, ...sellValuesForRenderInChart, null]
			: sellValuesForRenderInChart

	const rejectValuesWithPadding =
		daysBetweenDates === 2
			? [null, ...rejectValuesForRenderInChart, null]
			: rejectValuesForRenderInChart

	const series = [
		{
			name: 'Бракованное молоко',
			color: COLORS.error,
			data: shouldAggregateMonthly
				? yearsRejectValues
				: rejectValuesWithPadding,
		},
		{
			name: 'Товарное молоко',
			color: COLORS.blue,
			data: shouldAggregateMonthly ? yearsSellValues : sellValuesWithPadding,
		},
		{
			name: 'Нет данных',
			color: '#E0E0E0',
			data: shouldAggregateMonthly
				? yearsValues.map((_, i) =>
						(yearsValues[i].sell_sum === null ||
							yearsValues[i].sell_sum === 0) &&
						(yearsValues[i].reject_sum === null ||
							yearsValues[i].reject_sum === 0)
							? maxYearsSeriesValue + 1
							: null,
					)
				: daysBetweenDates === 2
					? [null, ...noDataValues, null]
					: noDataValues,
		},
	]

	const graphForTooltip = shouldAggregateMonthly
		? yearsValues.map(item => ({
				date: item.date,
				sell_milk_sum: item.sell_sum,
				reject_milk_sum: item.reject_sum,
				milkings: [],
			}))
		: graph

	const options: any = {
		cowCount,
		cattleCount: cowCount,
		seriesMeta: { graph: graphForTooltip },
		colors: [COLORS.main, COLORS.inputBorder, COLORS.error],
		mask: false,
		chart: {
			animations: {
				enabled: false,
			},
			zoom: {
				enabled: false,
			},
			stacked: true,
			locales: localizationApexCharts(),
			defaultLocale: 'ru',

			height: 250,
			type: 'bar',
			toolbar: {
				show: false,
			},
		},
		title: {
			style: {
				fontSize: '12px',
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				fontWeight: 400,
				lineHeight: '15px',
				color: '#7F7F84',
			},
			text: 'Вес, кг',
		},

		dataLabels: {
			enabled: false,
		},
		grid: {
			padding: {
				left: 0,
				right: 0,
			},
			strokeDashArray: [6, 3],
			show: true,
			position: 'back',
			xaxis: {
				lines: {
					show: true,
				},
			},
		},

		plotOptions: {
			bar: {
				horizontal: false,
				borderRadius:
					daysBetweenDates <= 1
						? 20
						: daysBetweenDates <= 60
							? 8
							: daysBetweenDates <= 91
								? 4
								: 8,
				borderRadiusApplication: 'end',
				borderRadiusWhenStacked: 'last',
				columnWidth: getDynamicColumnWidth(
					shouldAggregateMonthly ? dataXasis : chartCategories,
				),

				dataLabels: {
					hideOverflowingLabels: true,
					total: {
						offsetX: 200,
						enabled: false,
						style: {
							fontSize: '13px',
							fontWeight: 900,
						},
					},
				},
			},
		},
		fill: {
			opacity: 1,
		},
		xaxis: {
			crosshairs: {
				show: false,
			},
			type: 'category',
			tickPlacement: 'between',
			tickAmount: 31,
			tooltip: {
				enabled: false,
			},
			labels: {
				rotate: 0,
				offsetX: daysBetweenDates === 2 ? 50 : 0,
				trim: false,
				hideOverlappingLabels: false,
				formatter: function (value: string) {
					if (!value) {
						return ''
					}

					return moment(value).format('DD.MM')
				},

				datetimeUTC: false,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
			},
			categories: shouldAggregateMonthly ? dataXasis : chartCategories,
		},
		yaxis: {
			crosshairs: {
				show: false,
			},
			tickAmount: 4,
			min: 0,
			max: shouldAggregateMonthly
				? maxYearsSeriesValue + 1
				: maxSeriesValue + 1,
			labels: {
				offsetX: -10,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number, index: number) {
					if (index % 2 !== 0) {
						return ''
					}
					return Math.round(value).toLocaleString().toString()
				},
			},
		},
		states: {
			normal: {
				filter: {
					type: 'none',
					value: 0,
				},
			},
			hover: {
				filter: {
					type: 'none',
				},
			},
			active: {
				allowMultipleDataPointsSelection: false,
				filter: {
					type: 'none',
					value: 0,
				},
			},
		},
		tooltip: {
			custom: getMilkYieldTooltip,
		},
		legend: {
			show: false,
		},
	}
	return (
		<div id={'#chart'}>
			<div style={{ marginLeft: '-8px' }} id={'consuptionChart'}>
				<ReactApexChart
					key={`chart-${daysBetweenDates}`}
					id={'apex-chart'}
					options={options}
					series={series}
					type='bar'
					height={250}
					className={'apex-chart'}
				/>
			</div>
		</div>
	)
}

export default MilkYieldChart
